import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { ROLE_LABELS } from '@acc/domain';
import { PageHead, useAgency } from '../agency';

interface Preview { member: { membershipId: string; displayName: string | null; email: string; role: keyof typeof ROLE_LABELS; status: string }; quotes: number; requotes: number; calls: number }

export function Offboard() {
  const a = useAgency();
  const { membershipId = '' } = useParams();
  const api = useApiClient();
  const nav = useNavigate();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const q = useQuery<Preview>(a.manager ? a.path(`/team/${membershipId}/offboard-preview`) : null);
  const [replacement, setReplacement] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!a.manager) return <Banner tone="warn">Only the agency owner or office manager can deactivate team members.</Banner>;
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  const { member, quotes, requotes, calls } = q.data;
  const name = member.displayName ?? member.email;
  // Already suspended: only the work moves (baseline 9.21, suspend now and reassign later).
  const gone = member.status === 'Disabled';
  const candidates = a.team.filter((m) => m.membershipId !== member.membershipId && m.status !== 'Disabled');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await api<{ quotes: number; calls: number }>(a.path(`/team/${member.membershipId}/offboard`), { method: 'POST', body: { replacementMembershipId: replacement, confirm: true } });
      a.reloadTeam(); refreshCounts();
      a.notify('ok', `${gone ? 'Work reassigned.' : `${name} deactivated.`} ${r.quotes} quote(s) and ${r.calls} call(s) moved to ${a.name(replacement)}.`);
      nav(`${a.base}/team`);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="crumbs"><Link to={`${a.base}/team`}>Team</Link> / {gone ? 'Reassign work from' : 'Deactivate'} {name}</div>
      <PageHead title={gone ? `Reassign ${name}'s work` : `Deactivate ${name}`} sub={`${ROLE_LABELS[member.role]} · ${member.email}`} />
      {error && <Banner tone="bad">{error}</Banner>}
      <form className="card" onSubmit={submit}>
        <h2>Open work that will move</h2>
        <div className="cards" style={{ marginBottom: 12 }}>
          <div className="stat"><div className="num">{quotes}</div><div className="lbl">Open quotes</div></div>
          <div className="stat"><div className="num">{requotes}</div><div className="lbl">of which requotes / follow-up</div></div>
          <div className="stat"><div className="num">{calls}</div><div className="lbl">Open calls</div></div>
        </div>
        <p className="sub">Completed work and all history stay credited to {name}.{gone ? ` ${name} is already suspended.` : ' They lose access to the agency immediately.'}</p>
        <div className="form-grid" style={{ marginTop: 12 }}>
          <label className="field full">Move everything to
            <select required value={replacement} onChange={(e) => setReplacement(e.target.value)}>
              <option value="">Select a replacement…</option>
              {candidates.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email} ({ROLE_LABELS[m.role]})</option>)}
            </select>
          </label>
          <label className="full" style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            {gone ? `I understand ${name}'s open work will be transferred.` : `I understand ${name} will be deactivated and their open work transferred.`}
          </label>
        </div>
        <div className="actions">
          <Link className="btn" to={`${a.base}/team`}>Cancel</Link>
          <button className="btn primary danger" style={{ color: '#fff', background: 'var(--bad)', borderColor: 'var(--bad)' }} type="submit" disabled={busy || !replacement || !confirmed}>{busy ? 'Saving…' : gone ? 'Transfer work' : 'Deactivate and transfer'}</button>
        </div>
      </form>
    </>
  );
}
